/**
 * Correo (1.0 AdminOutreach + 27/10): escribes en español y cada destinatario lo recibe en su
 * idioma. Destinatarios elegidos buscando por nombre, email, @usuario o id (varios a la vez), o
 * envío masivo por país / a todos con prueba en seco. Enlaces como botones y adjuntos (máx. 5,
 * 10 MB en total) que se suben a Storage y la función mete en el correo. Respeta «marketing» en el
 * masivo salvo aviso de servicio. En sandbox todo va a EMAIL_REDIRECT_TO. Queda en el auditLog.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';
import { api } from '@nuvvu/shared/api';
import type { AdminUserRow } from '@nuvvu/shared/api';
import { COUNTRY_SEED } from '@nuvvu/shared';
import { call, errorText } from '../api.js';
import { auth, storage } from '../firebase.js';
import { btn, card, field, muted, Notice } from '../lib/ui.js';

interface Attachment {
  name: string;
  path: string;
  size: number;
}
interface LinkRow {
  label: string;
  url: string;
}

const MAX_TOTAL = 10 * 1024 * 1024;
const kb = (n: number): string =>
  n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`;
const who = (u: AdminUserRow): string =>
  [u.full_name, u.username ? `@${u.username}` : null, u.email].filter(Boolean).join(' · ') || u.uid;

export function OutreachPage(): ReactElement {
  const [params] = useSearchParams();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<AdminUserRow[] | null>(null);
  const [picked, setPicked] = useState<AdminUserRow[]>([]);
  const [country, setCountry] = useState('ALL');
  const [respect, setRespect] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // `/correo?uid=…` (desde la ficha del usuario): lo deja ya elegido.
  useEffect(() => {
    const uid = params.get('uid');
    if (!uid) return;
    call(api.admin.searchUsers, { q: uid })
      .then((r) => {
        const u = r.users.find((x) => x.uid === uid);
        if (u) setPicked([u]);
      })
      .catch(() => undefined);
  }, [params]);

  const cleanLinks = links.filter((l) => l.label.trim() && l.url.trim());
  const badLink = cleanLinks.find((l) => !/^https:\/\/\S+$/.test(l.url.trim()));
  const ready = subject.trim().length > 0 && message.trim().length > 0 && !badLink && !uploading;
  const extras = {
    ...(cleanLinks.length
      ? { links: cleanLinks.map((l) => ({ label: l.label.trim(), url: l.url.trim() })) }
      : {}),
    ...(files.length ? { attachments: files.map((f) => ({ name: f.name, path: f.path })) } : {}),
  };

  const search = (): void => {
    if (q.trim().length < 2) return;
    setResults(null);
    setError(null);
    call(api.admin.searchUsers, { q: q.trim() })
      .then((r) => {
        setResults(r.users);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setResults([]);
      });
  };
  const pick = (u: AdminUserRow): void => {
    setPicked((p) => (p.some((x) => x.uid === u.uid) ? p : [...p, u]));
  };

  const upload = async (list: FileList | null): Promise<void> => {
    const uid = auth.currentUser?.uid;
    if (!list || !uid) return;
    const incoming = [...list];
    const total = files.reduce((s, f) => s + f.size, 0) + incoming.reduce((s, f) => s + f.size, 0);
    if (files.length + incoming.length > 5) {
      setError('Máximo 5 adjuntos.');
      return;
    }
    if (total > MAX_TOTAL) {
      setError('Los adjuntos no pueden pasar de 10 MB en total.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const added: Attachment[] = [];
      for (const f of incoming) {
        const safe = f.name.replace(/[^\w.\-() ]+/g, '_').slice(0, 180) || 'archivo';
        const path = `admin_mail/${uid}/${String(Date.now())}-${safe}`;
        await uploadBytes(ref(storage, path), f, {
          contentType: f.type || 'application/octet-stream',
        });
        added.push({ name: f.name.slice(0, 160), path, size: f.size });
      }
      setFiles((cur) => [...cur, ...added]);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setUploading(false);
    }
  };
  const removeFile = (a: Attachment): void => {
    setFiles((cur) => cur.filter((f) => f.path !== a.path));
    deleteObject(ref(storage, a.path)).catch(() => undefined);
  };

  const sendPicked = (): void => {
    if (!ready || picked.length === 0) return;
    if (
      picked.length > 1 &&
      !window.confirm(`¿Enviar «${subject}» a ${String(picked.length)} usuarios?`)
    )
      return;
    setBusy(true);
    setError(null);
    call(api.admin.sendUserEmail, { uids: picked.map((u) => u.uid), subject, message, ...extras })
      .then((r) => {
        setNotice(
          `Enviados ${String(r.sent)} de ${String(r.total)}` +
            (r.failed ? ` (${String(r.failed)} fallos)` : '') +
            (r.skipped.length ? ` · ${String(r.skipped.length)} sin email` : '') +
            '.',
        );
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const bulk = (dry: boolean): void => {
    if (!ready) return;
    if (
      !dry &&
      !window.confirm(
        `¿Enviar «${subject}» a ${country === 'ALL' ? 'TODOS los usuarios' : `los usuarios de ${country}`}?`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    call(api.admin.sendBulkEmail, {
      country,
      subject,
      message,
      respect_marketing: respect,
      ...(dry ? { dry_run: true } : extras),
    })
      .then((r) => {
        setNotice(
          r.dry_run
            ? `Prueba en seco: ${String(r.total)} destinatarios.`
            : `Enviados ${String(r.sent)} de ${String(r.total)} (${String(r.failed)} fallos).`,
        );
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const label = { display: 'grid', gap: 4, fontSize: 13 } as const;
  return (
    <>
      <h1 style={{ margin: 0 }}>Correo a usuarios</h1>
      <p style={muted}>
        Escribe en español: cada usuario lo recibe traducido a su idioma (asunto, texto y botones).
        Tarjeta de email de nuvvu con el asunto como título. En sandbox todo va a la dirección de
        redirección. Queda en el auditLog.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      <section style={{ ...card, display: 'grid', gap: 10, maxWidth: 760 }}>
        <label style={label}>
          Asunto
          <input
            style={field}
            value={subject}
            maxLength={200}
            onChange={(e) => {
              setSubject(e.target.value);
            }}
          />
        </label>
        <label style={label}>
          Mensaje
          <textarea
            style={{ ...field, minHeight: 160 }}
            value={message}
            maxLength={12000}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
          />
        </label>
        <div style={{ display: 'grid', gap: 6 }}>
          <strong style={{ fontSize: 13 }}>Enlaces (botones bajo el texto)</strong>
          {links.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input
                style={{ ...field, flex: '1 1 160px' }}
                placeholder="Texto del botón"
                value={l.label}
                maxLength={80}
                onChange={(e) => {
                  setLinks(links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)));
                }}
              />
              <input
                style={{ ...field, flex: '2 1 260px' }}
                placeholder="https://…"
                value={l.url}
                onChange={(e) => {
                  setLinks(links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)));
                }}
              />
              <button
                type="button"
                style={btn(false, true)}
                onClick={() => {
                  setLinks(links.filter((_, j) => j !== i));
                }}
              >
                Quitar
              </button>
            </div>
          ))}
          {badLink && (
            <span style={{ ...muted, color: '#C2410C' }}>
              Los enlaces deben empezar por https://
            </span>
          )}
          {links.length < 10 && (
            <button
              type="button"
              style={{ ...btn(false), justifySelf: 'start' }}
              onClick={() => {
                setLinks([...links, { label: '', url: '' }]);
              }}
            >
              + Añadir enlace
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <strong style={{ fontSize: 13 }}>Adjuntos (máx. 5, 10 MB en total)</strong>
          {files.map((f) => (
            <div key={f.path} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>📎 {f.name}</span>
              <span style={muted}>{kb(f.size)}</span>
              <button
                type="button"
                style={btn(false, true)}
                onClick={() => {
                  removeFile(f);
                }}
              >
                Quitar
              </button>
            </div>
          ))}
          {files.length < 5 && (
            <input
              type="file"
              multiple
              disabled={uploading}
              onChange={(e) => {
                void upload(e.target.files);
                e.target.value = '';
              }}
            />
          )}
          {uploading && <span style={muted}>Subiendo…</span>}
        </div>
      </section>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 12,
          alignItems: 'start',
        }}
      >
        <section style={{ ...card, display: 'grid', gap: 10 }}>
          <strong>A usuarios concretos</strong>
          <form
            style={{ display: 'flex', gap: 6 }}
            onSubmit={(e) => {
              e.preventDefault();
              search();
            }}
          >
            <input
              style={{ ...field, flex: 1 }}
              placeholder="Nombre, email, @usuario o id"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
              }}
            />
            <button type="submit" style={btn(false)} disabled={q.trim().length < 2}>
              Buscar
            </button>
          </form>
          {results?.length === 0 && <span style={muted}>Sin resultados.</span>}
          {results && results.length > 0 && (
            <div style={{ display: 'grid', gap: 4, maxHeight: 280, overflowY: 'auto' }}>
              {results.map((u) => {
                const already = picked.some((x) => x.uid === u.uid);
                return (
                  <div
                    key={u.uid}
                    style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}
                  >
                    <span style={{ flex: 1 }}>
                      {who(u)}
                      {u.country_code ? ` · ${u.country_code}` : ''}
                      {u.banned ? ' · vetado' : ''}
                    </span>
                    <button
                      type="button"
                      style={btn(!already)}
                      disabled={already || !u.email}
                      onClick={() => {
                        pick(u);
                      }}
                    >
                      {already ? 'Añadido' : u.email ? 'Añadir' : 'Sin email'}
                    </button>
                  </div>
                );
              })}
              {results.length > 1 && (
                <button
                  type="button"
                  style={{ ...btn(false), justifySelf: 'start' }}
                  onClick={() => {
                    results.filter((u) => u.email).forEach(pick);
                  }}
                >
                  Añadir todos
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {picked.map((u) => (
              <span
                key={u.uid}
                style={{
                  display: 'inline-flex',
                  gap: 6,
                  alignItems: 'center',
                  border: '1px solid #ddd',
                  borderRadius: 999,
                  padding: '4px 10px',
                  fontSize: 12,
                }}
              >
                <Link to={`/usuarios/${u.uid}`}>
                  {u.full_name ?? u.username ?? u.email ?? u.uid}
                </Link>
                <button
                  type="button"
                  aria-label="Quitar"
                  style={{ border: 0, background: 'none', cursor: 'pointer' }}
                  onClick={() => {
                    setPicked(picked.filter((x) => x.uid !== u.uid));
                  }}
                >
                  ✕
                </button>
              </span>
            ))}
            {picked.length === 0 && <span style={muted}>Ningún destinatario elegido.</span>}
          </div>
          <button
            type="button"
            style={{ ...btn(true), justifySelf: 'start' }}
            disabled={busy || !ready || picked.length === 0}
            onClick={sendPicked}
          >
            {picked.length > 1 ? `Enviar a ${String(picked.length)} usuarios` : 'Enviar'}
          </button>
        </section>
        <section style={{ ...card, display: 'grid', gap: 10 }}>
          <strong>Masivo</strong>
          <label style={label}>
            País
            <select
              style={field}
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
              }}
            >
              <option value="ALL">Todos los países</option>
              {COUNTRY_SEED.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={respect}
              onChange={(e) => {
                setRespect(e.target.checked);
              }}
            />
            Solo a quien tiene «marketing» activado (desmarcar solo para avisos de servicio)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              style={btn(false)}
              disabled={busy || !ready}
              onClick={() => {
                bulk(true);
              }}
            >
              Contar destinatarios
            </button>
            <button
              type="button"
              style={btn(true)}
              disabled={busy || !ready}
              onClick={() => {
                bulk(false);
              }}
            >
              Enviar a todos
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
